// Contratos compartilhados entre API e web. Envelope conforme PADRAO §4.5:
// sucesso em { data }, erro em { error, message, statusCode }.

export interface RespostaOk<T> {
  data: T;
}

export interface RespostaErro {
  error: string;
  message: string;
  statusCode: number;
}

export type Resposta<T> = RespostaOk<T> | RespostaErro;

export function ehErro<T>(r: Resposta<T>): r is RespostaErro {
  return (r as RespostaErro).error !== undefined;
}

export type TipoLeitura =
  | 'glicemia'
  | 'pa_sistolica'
  | 'pa_diastolica'
  | 'frequencia_cardiaca'
  | 'saturacao'
  | 'temperatura'
  | 'frequencia_respiratoria'
  | 'diurese'
  | 'evacuacoes'
  | 'dieta_enteral'
  | 'balanco_hidrico'
  | 'peso';

export type OrigemLeitura = 'ble' | 'manual' | 'sensor_continuo' | 'exame';

/** Unidade canonica de cada tipo. API e banco guardam sempre nesta unidade. */
export const UNIDADE_POR_TIPO: Record<TipoLeitura, string> = {
  glicemia: 'mg/dL',
  pa_sistolica: 'mmHg',
  pa_diastolica: 'mmHg',
  frequencia_cardiaca: 'bpm',
  saturacao: '%',
  temperatura: 'C',
  frequencia_respiratoria: 'irpm',
  diurese: 'mL',
  evacuacoes: 'un',
  dieta_enteral: 'mL/h',
  balanco_hidrico: 'mL',
  peso: 'kg',
};

/**
 * Faixas de plausibilidade — NAO sao faixas clinicas de normalidade.
 * Servem so para barrar digitacao absurda (glicemia 2130 por dedo escorregado).
 * Limiar de alerta clinico e outro assunto, e vem na etapa de avisos.
 */
/**
 * A faixa USUAL de adulto de cada sinal — a faixa verde do gráfico dia a dia.
 *
 * Não é alvo terapêutico dela (quem define é a equipe): é referência para o olho
 * ver de relance se a curva anda dentro ou fora do comum. A glicemia usa o alvo
 * de paciente internado (70–180), o mesmo da folha de laboratório, e não o
 * jejum de ambulatório. Sinal sem faixa aqui sai no gráfico sem faixa verde.
 */
export const FAIXA_USUAL_SINAL: Partial<Record<TipoLeitura, { min: number; max: number }>> = {
  pa_sistolica: { min: 90, max: 140 },
  pa_diastolica: { min: 60, max: 90 },
  frequencia_cardiaca: { min: 60, max: 100 },
  saturacao: { min: 94, max: 100 },
  temperatura: { min: 36, max: 37.5 },
  glicemia: { min: 70, max: 180 },
  frequencia_respiratoria: { min: 12, max: 20 },
};

export const FAIXA_PLAUSIVEL: Record<TipoLeitura, { min: number; max: number }> = {
  glicemia: { min: 10, max: 900 },
  pa_sistolica: { min: 40, max: 300 },
  pa_diastolica: { min: 20, max: 200 },
  frequencia_cardiaca: { min: 20, max: 250 },
  saturacao: { min: 30, max: 100 },
  temperatura: { min: 28, max: 45 },
  frequencia_respiratoria: { min: 4, max: 80 },
  diurese: { min: 0, max: 10000 },
  evacuacoes: { min: 0, max: 30 },
  dieta_enteral: { min: 0, max: 500 },
  balanco_hidrico: { min: -10000, max: 10000 },
  peso: { min: 20, max: 300 },
};

export interface LeituraDTO {
  id: string;
  tipo: TipoLeitura;
  valor: number;
  unidade: string;
  origem: OrigemLeitura;
  /** Hora da verdade: quando o sistema recebeu. */
  recebidoEm: string;
  /** Quando a medida aconteceu. */
  medidoEm: string;
  /** O que o relogio do aparelho dizia — metadado, costuma estar errado. */
  medidoEmAparelho: string | null;
  grupo: string | null;
  /**
   * Medida de teste: existe para a tela nao ficar vazia, e NAO e historico.
   * Relatorio, grafico e media ignoram.
   */
  ehReferencia?: boolean;
  /** `tipo` importa na tela: só pulseira de FC pode aparecer como "ao vivo". */
  aparelho: { id: string; apelido: string; tipo: TipoAparelho } | null;
  autor: { id: string; nome: string } | null;
}

export type TipoAparelho =
  | 'glicosimetro'
  | 'pressao'
  | 'cardio'
  | 'oximetro'
  | 'sensor_continuo';

export interface AnotacaoDTO {
  id: string;
  texto: string;
  ocorreuEm: string;
  criadoEm: string;
  autor: { id: string; nome: string };
}

export interface UsuarioDTO {
  /** Como a pessoa se relaciona com a paciente: filho, filha, neta. */
  parentesco?: string | null;
  /** Como a pessoa entra: "ilton", "ildete". */
  usuario?: string;
  id: string;
  nome: string;
  email?: string | null;
  perfil: 'admin_geral' | 'familiar' | 'leitor';
  /**
   * A senha em uso foi entregue pronta e ainda nao foi trocada pelo dono.
   *
   * Enquanto for `true`, a API recusa tudo fora a troca — e a tela leva a
   * pessoa direto para ela.
   */
  senhaProvisoria?: boolean;
}
